import React, { memo, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { Skeleton } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { monthlyReportObjFormate } from "../../../../utils/chartKeyFormat";

const SOFT_PALETTE = {
  blue: "#4F7CFF",
  teal: "#24C8A5",
  orange: "#F5A524",
  cyan: "#2AA7E0",
  text: "#0F172A",
  muted: "#475569",
  grid: "rgba(148,163,184,0.15)",
};

const fontFamily = '"Gilroy Regular", "Gilroy", Inter, "Segoe UI", sans-serif';

const toRgba = (color, alpha = 0.14) => {
  if (!color || typeof color !== "string") return `rgba(79, 124, 255, ${alpha})`;
  const hex = color.replace("#", "");
  if (hex.length !== 6) return color;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((value) => Number.isNaN(value))) return color;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(Math.abs(Number(value) || 0));

const LineChart = ({
  dataLabel,
  dataValues,
  extraDataSets = [],
  chartDataSet,
  loadingState,
  filterOnchange,
  vendorChart,
  customCardClass,
  durationValue,
  chooseChartDuration,
  disableDuration,
  xTickLimit = 8,
  onCardClick,
  inline,
  chartHeight = 360,
  yAxisValueFormatter,
  tooltipValueFormatter,
  showHeading = true,
}) => {
  const [formattedLabels, setFormattedLabels] = useState([]);
  const [tooltip, setTooltip] = useState(null);

  const normalizedBarColor = Array.isArray(chartDataSet?.barColor)
    ? chartDataSet.barColor[0]
    : chartDataSet?.barColor;
  const primaryColor =
    normalizedBarColor ||
    (Array.isArray(chartDataSet?.barColors) ? chartDataSet.barColors[0] : null) ||
    SOFT_PALETTE.blue;

  const safeValues = Array.isArray(dataValues) ? dataValues : [];
  const computedMax = safeValues.length ? Math.max(...safeValues.map(Number)) : 0;
  const yStepSize = computedMax > 0 ? Math.ceil(computedMax / 4) : 1;
  const axisTickFont = {
    size: 14,
    family: fontFamily,
    weight: "400",
  };

  useEffect(() => {
    const safeLabels = Array.isArray(dataLabel) ? dataLabel : [];
    let nl = [];
    if (durationValue === "monthly") {
      nl = monthlyReportObjFormate(safeLabels).reverse();
    } else {
      nl = safeLabels;
    }
    setFormattedLabels(nl);
  }, [durationValue, dataLabel]);

  const dataSets = {
    labels: formattedLabels || [],
    datasets: [
      {
        label: chartDataSet?.chartLabels?.[0] || chartDataSet?.cardHeading,
        borderWidth: 2.5,
        data: dataValues,
        borderColor: primaryColor,
        backgroundColor: (context) => {
          const chart = context?.chart;
          const area = chart?.chartArea;
          if (!area) return toRgba(primaryColor, 0.14);
          const gradient = chart.ctx.createLinearGradient(0, area.bottom, 0, area.top);
          gradient.addColorStop(0, toRgba(primaryColor, 0.02));
          gradient.addColorStop(1, toRgba(primaryColor, 0.18));
          return gradient;
        },
        fill: true,
        tension: 0.42,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: "#fff",
        pointBorderColor: primaryColor,
        pointBorderWidth: 2,
      },
      ...extraDataSets.map((set) => ({
        label: set?.label,
        borderWidth: 2.5,
        data: set?.data || [],
        borderColor: set?.borderColor || primaryColor,
        backgroundColor: toRgba(set?.borderColor || primaryColor, 0.08),
        fill: true,
        tension: 0.42,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: "#fff",
        pointBorderColor: set?.borderColor || primaryColor,
        pointBorderWidth: 2,
      })),
    ],
  };

  const options = {
    animation: {
      duration: 400,
      easing: "easeInOutCubic",
    },
    plugins: {
      legend: false,
      tooltip: {
        enabled: false,
        external: (context) => {
          const { chart, tooltip: model } = context;
          if (!chart) return;
          if (!model || model.opacity === 0) {
            setTooltip((prev) => (prev ? { ...prev, visible: false } : prev));
            return;
          }
          const point = model.dataPoints?.[0];
          if (!point) return;
          const rect = chart.canvas.getBoundingClientRect();
          const formattedValue =
            typeof tooltipValueFormatter === "function"
              ? tooltipValueFormatter(point.parsed?.y ?? point.raw ?? 0)
              : formatNumber(point.parsed?.y ?? point.raw ?? 0);
          setTooltip({
            visible: true,
            x: rect.left + model.caretX,
            y: rect.top + model.caretY,
            title: chartDataSet?.cardHeading || chartDataSet?.chartLabels?.[0] || "",
            value: formattedValue,
          });
        },
        backgroundColor: "#FFFFFF",
        titleColor: "#0F172A",
        bodyColor: "#0F172A",
        borderColor: "rgba(226,232,240,0.9)",
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        displayColors: false,
        titleFont: { size: 14, family: fontFamily, weight: "400" },
        bodyFont: { size: 14, family: fontFamily, weight: "400" },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: SOFT_PALETTE.grid,
          drawBorder: false,
        },
        border: { display: false },
        ticks: {
          autoSkip: true,
          maxTicksLimit: xTickLimit,
          color: SOFT_PALETTE.muted,
          font: axisTickFont,
          padding: 12,
        },
      },
      y: {
        grid: {
          display: true,
          color: SOFT_PALETTE.grid,
          drawBorder: false,
        },
        ticks: {
          stepSize: yStepSize,
          beginAtZero: true,
          color: SOFT_PALETTE.muted,
          font: axisTickFont,
          padding: 12,
          callback: (v) => (typeof yAxisValueFormatter === "function" ? yAxisValueFormatter(v) : v.toLocaleString("en-IN")),
        },
      },
    },
    maintainAspectRatio: false,
  };

  const heading = (
    <div style={{ marginBottom: "0.35rem" }}>
      <div
        style={{
          fontSize: "18px",
          fontFamily: fontFamily,
          color: "#0F172A",
          fontWeight: 600,
          marginBottom: "0.35rem",
        }}
      >
        {chartDataSet?.cardHeading}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
        {chartDataSet?.chartIndicators?.map((e) => (
          <div key={e.value} style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: e?.color || primaryColor, display: "inline-block" }} />
            <span style={{ fontSize: "13px", color: SOFT_PALETTE.muted, fontFamily: fontFamily, fontWeight: 400 }}>{e.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const indicatorOnlyHeading = (
    <div style={{ marginBottom: "0.35rem", display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
      {chartDataSet?.chartIndicators?.map((e) => (
        <div key={e.value} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: e?.color || primaryColor, display: "inline-block" }} />
          <span style={{ fontSize: "13px", color: SOFT_PALETTE.muted, fontFamily: fontFamily, fontWeight: 400 }}>{e.value}</span>
        </div>
      ))}
    </div>
  );

  const tooltipStyle = tooltip?.visible
    ? { opacity: 1, transform: "translateY(-6px)" }
    : { opacity: 0, transform: "translateY(0px)" };

  const chartInner = (
    <div style={{ width: "100%", position: "relative" }}>
      <div style={{ width: "100%", height: chartHeight, marginTop: 4 }}>
        <Line data={dataSets} options={options} />
      </div>
      {tooltip?.visible ? (
        <div
          style={{
            position: "fixed",
            left: tooltip.x + 14,
            top: tooltip.y - 12,
            pointerEvents: "none",
            zIndex: 60,
            minWidth: 180,
            background: "#FFFFFF",
            border: "1px solid rgba(226,232,240,0.9)",
            borderRadius: 12,
            boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
            padding: "10px 12px",
            color: SOFT_PALETTE.text,
            fontFamily: fontFamily,
            fontSize: 14,
            lineHeight: 1.5,
            opacity: tooltipStyle.opacity,
            transform: tooltipStyle.transform,
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          <div style={{ color: SOFT_PALETTE.muted, marginBottom: 4 }}>{tooltip.title}</div>
          <div style={{ fontWeight: 400, color: SOFT_PALETTE.text }}>{tooltip.value}</div>
        </div>
      ) : null}
    </div>
  );

  const skeleton = (
    <>
      <Skeleton width="180px" height="24px" style={{ marginBottom: 10 }} />
      <Skeleton width="100%" height={Math.max(260, chartHeight)} variant="rectangular" style={{ borderRadius: 16, backgroundColor: "rgba(226,232,240,0.6)" }} />
    </>
  );

  if (inline) {
    return <div onClick={onCardClick} style={{ cursor: onCardClick ? "pointer" : "inherit", width: "100%" }}>{loadingState ? skeleton : <>{showHeading ? heading : indicatorOnlyHeading}{chartInner}</>}</div>;
  }

  return (
    <div
      className={customCardClass}
      onClick={onCardClick}
      style={{
        background: "#fff",
        borderRadius: "18px",
        border: "1px solid rgba(226,232,240,0.7)",
        boxShadow: "0px 8px 30px rgba(15,23,42,0.06)",
        padding: "20px",
        minHeight: "420px",
        cursor: onCardClick ? "pointer" : "default",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {loadingState ? skeleton : <>{showHeading ? heading : indicatorOnlyHeading}{chartInner}</>}
    </div>
  );
};

export default memo(LineChart);
